import { z } from "zod";

import { customFieldSchema } from "../basics/custom";
import { customSchema } from "../sections";
import { defaultUrl, urlSchema } from "../shared/url";
import { defaultCoverLetterLayout } from "./layout";

export {
  DEFAULT_COVER_LETTER_SECTIONS,
  defaultCoverLetterLayout,
  flattenCoverLetterLayoutItems,
  insertBeforeClosing,
  insertBeforeClosingInPlace,
  resolveCoverLetterLayout,
} from "./layout";

// --- Sender Schema ---
export const clSenderSchema = z.object({
  name: z.string(),
  email: z.literal("").or(z.string().email()),
  phone: z.string(),
  location: z.string(),
  url: urlSchema,
  linkedinUrl: urlSchema,
  picture: z.object({
    url: z.string(),
    size: z.number().default(64),
    aspectRatio: z.number().default(1),
    borderRadius: z.number().default(0),
    effects: z.object({
      hidden: z.boolean().default(false),
      border: z.boolean().default(false),
      grayscale: z.boolean().default(false),
    }),
  }),
  customFields: z.array(customFieldSchema),
});

export type CLSender = z.infer<typeof clSenderSchema>;

export const defaultCLSender: CLSender = {
  name: "",
  email: "",
  phone: "",
  location: "",
  url: defaultUrl,
  linkedinUrl: defaultUrl,
  picture: {
    url: "",
    size: 64,
    aspectRatio: 1,
    borderRadius: 0,
    effects: { hidden: false, border: false, grayscale: false },
  },
  customFields: [],
};

// --- Recipient Schema ---
export const clRecipientSchema = z.object({
  name: z.string(),
  title: z.string(),
  company: z.string(),
  department: z.string(),
  address: z.string(),
});

export type CLRecipient = z.infer<typeof clRecipientSchema>;

export const defaultCLRecipient: CLRecipient = {
  name: "",
  title: "",
  company: "",
  department: "",
  address: "",
};

// --- Letter Schema ---
export const clLetterSchema = z.object({
  date: z.string(),
  subject: z.string(),
  greeting: z.string(),
  opening: z.string(),
  body: z.string(),
  closing: z.string(),
  signOff: z.string(),
  enclosures: z.string(),
});

export type CLLetter = z.infer<typeof clLetterSchema>;

export const defaultCLLetter: CLLetter = {
  date: "",
  subject: "",
  greeting: "Dear Hiring Manager,",
  opening: "",
  body: "",
  closing: "",
  signOff: "Sincerely,",
  enclosures: "",
};

// --- Reference Schema ---
export const clReferenceSchema = z.object({
  jobTitle: z.string(),
  referenceNumber: z.string(),
  source: z.string(),
});

export type CLReference = z.infer<typeof clReferenceSchema>;

export const defaultCLReference: CLReference = {
  jobTitle: "",
  referenceNumber: "",
  source: "",
};

// --- Cover Letter Metadata Schema ---
export const clMetadataSchema = z.object({
  template: z.string().default("professional"),
  layout: z.array(z.array(z.array(z.string()))).default(defaultCoverLetterLayout),
  css: z.object({
    value: z.string().default(""),
    visible: z.boolean().default(false),
  }),
  page: z.object({
    margin: z.number().default(18),
    format: z.enum(["a4", "letter"]).default("a4"),
    options: z.object({
      breakLine: z.boolean().default(false),
      pageNumbers: z.boolean().default(false),
    }),
  }),
  theme: z.object({
    background: z.string().default("#ffffff"),
    text: z.string().default("#000000"),
    primary: z.string().default("#1e40af"),
  }),
  typography: z.object({
    font: z.object({
      family: z.string().default("IBM Plex Serif"),
      subset: z.string().default("latin"),
      variants: z.array(z.string()).default(["regular"]),
      size: z.number().default(14),
    }),
    lineHeight: z.number().default(1.6),
    hideIcons: z.boolean().default(false),
    underlineLinks: z.boolean().default(true),
  }),
  notes: z.string().default(""),
});

export type CLMetadata = z.infer<typeof clMetadataSchema>;

export const defaultCLMetadata: CLMetadata = {
  template: "professional",
  layout: defaultCoverLetterLayout,
  css: { value: "", visible: false },
  page: {
    margin: 18,
    format: "a4",
    options: { breakLine: false, pageNumbers: false },
  },
  theme: {
    background: "#ffffff",
    text: "#000000",
    primary: "#1e40af",
  },
  typography: {
    font: {
      family: "IBM Plex Serif",
      subset: "latin",
      variants: ["regular", "italic", "600"],
      size: 14,
    },
    lineHeight: 1.6,
    hideIcons: false,
    underlineLinks: true,
  },
  notes: "",
};

// --- Full Cover Letter Data Schema ---
export const coverLetterDataSchema = z.object({
  sender: clSenderSchema,
  recipient: clRecipientSchema,
  letter: clLetterSchema,
  reference: clReferenceSchema,
  custom: z.record(z.string(), customSchema).default({}),
  metadata: clMetadataSchema,
});

export type CoverLetterData = z.infer<typeof coverLetterDataSchema>;

export const defaultCoverLetterData: CoverLetterData = {
  sender: defaultCLSender,
  recipient: defaultCLRecipient,
  letter: defaultCLLetter,
  reference: defaultCLReference,
  custom: {},
  metadata: defaultCLMetadata,
};

export * from "./sample";
